import { useRender } from '@base-ui/react/use-render'
import { type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils.ts'

import { headingVariants } from './heading.variants.ts'

const levelTagNames: Record<1 | 2 | 3 | 4 | 5 | 6, 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'> = {
  1: 'h1',
  2: 'h2',
  3: 'h3',
  4: 'h4',
  5: 'h5',
  6: 'h6',
}

/**
 * Heading renders a section title in the heading typeface.
 *
 * `variant` controls the **visual** size (`h1`–`h6` and `subtitle`, matching the Figma styles). `level` controls the
 * rendered **HTML tag** (`<h{level}>`) and is decoupled from the visual size — pick the level that fits
 * the document outline, the variant that fits the visual hierarchy. When `level` is omitted it follows
 * the `variant` (`variant="h3"` → `<h3>`), and when `variant` is omitted it follows the `level`
 * (`level={3}` → h3 sizing). `subtitle` is visual-only and defaults to a `<span>`; pass `level` or
 * `render` for a semantic heading or another element.
 *
 * **Context:** sizes resolve to the **BO** skin by default. Wrap a subtree in `[data-typescale="fo"]`
 * (front-office) to switch to the responsive FO scale — Mobile sizes below 1024px, Desktop at/above —
 * and the Archivo heading font. The component API does not change between contexts; `font-heading`
 * resolves per active theme and context.
 *
 * @summary Section title, `h1`–`h6` and `subtitle` visual variants with a decoupled semantic `level`.
 */
function Heading(props: Heading.Props) {
  const { className, variant, level, weight, render, ref, ...rest } = props
  const tagName = level === undefined ? (variant === 'subtitle' ? 'span' : (variant ?? 'h1')) : levelTagNames[level]
  const resolvedVariant = variant ?? (level === undefined ? 'h1' : levelTagNames[level])
  return useRender({
    render,
    ref,
    defaultTagName: tagName,
    props: {
      'data-slot': 'heading',
      className: cn(headingVariants({ variant: resolvedVariant, weight }), className),
      ...rest,
    },
  })
}

namespace Heading {
  type Variants = NonNullable<VariantProps<typeof headingVariants>>
  export type Props = useRender.ComponentProps<'h1'> & {
    /** Visual size of the heading (Figma style name). @default the `level` tag, else 'h1' */
    variant?: NonNullable<Variants['variant']> | undefined
    /** Rendered `<h{level}>` tag, decoupled from `variant`. @default the `variant` digit */
    level?: 1 | 2 | 3 | 4 | 5 | 6
    /** Weight override; omit to keep the variant's baseline (600). @default undefined */
    weight?: NonNullable<Variants['weight']> | undefined
  }
}

export { Heading, headingVariants }
